// @flow
import api from '../api/page';
import { createSelector } from 'reselect';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';
import isEmpty from 'lodash/isEmpty';
import { LOGIN_SUCCESS } from './login';
import ms from 'ms';
import shuffle from 'lodash/shuffle';

/* Action Types */
export const LOAD_HOME_DATA_FAIL = 'la/domain/home/LOAD_FAIL';
export const LOAD_HOME_DATA_REQUEST = 'la/domain/home/LOAD_REQUEST';
export const LOAD_HOME_DATA_SUCCESS = 'la/domain/home/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('5m');

// reducer
const DEFAULT_STATE = {
    acn: {},
    featuredCatalogs: [],
    loaded: 0,
    loading: false,
    trendingItems: [],
    upcomingCatalogs: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let featuredCatalogs;

    switch (action.type) {
        case LOAD_HOME_DATA_FAIL:
            return {
                ...state,
                loading: false,
            };
        case LOAD_HOME_DATA_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_HOME_DATA_SUCCESS:
            // featuredCatalogs
            if (Array.isArray(action.payload.featuredCatalogs)) {
                if (Number.isInteger(action.payload.featuredCatalogs[0])) {
                    // old way, an array of ints
                    featuredCatalogs = [...action.payload.featuredCatalogs];
                } else {
                    // new way, an array of objects
                    featuredCatalogs = action.payload.featuredCatalogs.map(x => x.catalogId);
                }
            } else {
                // bad, not an array
                featuredCatalogs = [];
            }
            // TODO: make this reducer pure by moving the shuffle to the selector
            featuredCatalogs = shuffle(featuredCatalogs);
            return {
                ...state,
                acn: { ...action.payload.acn },
                featuredCatalogs,
                loaded: action.meta.actionTime,
                loading: false,
                trendingItems: [...action.payload.trendingItems],
                upcomingCatalogs: [...action.payload.upcomingCatalogs],
            };
        case LOGIN_SUCCESS:
            return DEFAULT_STATE;
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.home;

export const shouldFetchHomeData = createSelector(stateSelector, state => {
    const { loaded, loading } = state;
    if (loading) return false;

    if (loaded || isEmpty(state.featuredCatalogs)) {
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) return false;
    }
    return true;
});

/* ACTION CREATORS */
const loadHomeData = forceUpdate => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        const authToken = getAuthToken(state);
        dispatch({
            type: LOAD_HOME_DATA_REQUEST,
        });
        const response = await api.fetchHomeData({ authToken, deployment, forceUpdate });
        dispatch({
            meta: { actionTime: Date.now() },
            payload: response.data,
            type: LOAD_HOME_DATA_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_HOME_DATA_FAIL,
        });
    }
};

export const fetchHomeDataIfNeeded = (forceUpdate: boolean = false) => async (
    dispatch: Function,
    getState: Function
) => {
    if (forceUpdate || shouldFetchHomeData(getState())) {
        return dispatch(loadHomeData(forceUpdate));
    }
    return Promise.resolve();
};
